/* import __COLOCATED_TEMPLATE__ from './outbound-campaign-edit-panel.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type { ParseResult } from 'papaparse';
import csvParser from 'papaparse';
import { Promise as EmberPromise } from 'rsvp';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import ajax, { get } from 'embercom/lib/ajax';
import type PhoneNumber from 'embercom/models/calling-phone-number';
import type { FinVoiceProcedure } from 'embercom/objects/ai-agent/fin-voice/fin-voice-runbook-options';
import {
  FIN_VOICE_11LABS_VOICES,
  getVoicesForLanguage,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import type { Campaign } from './outbound-campaign-list';
import type {
  FinVoiceNumberConfigurationHandoverConfiguration,
  HandoverConfiguration,
  HandoverConfigurationTools,
  ScheduledConfigurationKey,
} from 'embercom/models/fin-voice/number-configuration';

interface OutboundCampaignEditPanelSignature {
  Args: {
    campaign: Campaign | null;
    phoneNumbers: Record<string, PhoneNumber>;
    onSave: () => void;
    onClose: () => void;
  };
}

const MAX_CSV_ROWS = 100;

const TIME_OPTIONS: { value: string; text: string }[] = (() => {
  let options: { value: string; text: string }[] = [];
  for (let hour = 0; hour < 24; hour++) {
    let hh = String(hour).padStart(2, '0');
    options.push({ value: `${hh}:00`, text: `${hh}:00` });
    options.push({ value: `${hh}:30`, text: `${hh}:30` });
  }
  return options;
})();

export default class OutboundCampaignEditPanel extends Component<OutboundCampaignEditPanelSignature> {
  @service declare intl: IntlService;
  @service declare appService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @tracked campaignName = this.args.campaign?.name || '';
  @tracked selectedPhoneNumberId: string | undefined = undefined;
  @tracked file: File | null = null;
  @tracked fileIsValid = false;
  @tracked fileFailedToUpload = false;
  @tracked parsingErrorMessage = '';
  @tracked parsedRows: { phone_number: string }[] = [];
  @tracked validationErrors: string[] = [];
  @tracked isParsed = false;

  @tracked isSaving = false;
  @tracked isSending = false;
  @tracked showSendConfirmModal = false;

  @tracked procedures: FinVoiceProcedure[] = [];
  @tracked isLoadingProcedures = false;
  @tracked selectedProcedureId: string | undefined = undefined;

  @tracked selectedVoice: string | undefined = undefined;
  @tracked voicemailEnabled = false;
  @tracked voicemailMessage = '';
  @tracked quietHoursEnabled = true;
  @tracked quietHoursStart = '09:00';
  @tracked quietHoursEnd = '21:00';

  @tracked configBeingEdited: ScheduledConfigurationKey = 'in';
  @tracked clonedHandoverConfig: FinVoiceNumberConfigurationHandoverConfiguration = {};
  @tracked autoCloseWhenNotEscalated = false;
  @tracked defaultTeamId: string | undefined = undefined;

  private readonly E164_REGEX = /^\+[1-9]\d{1,14}$/;

  constructor(owner: unknown, args: OutboundCampaignEditPanelSignature['Args']) {
    super(owner, args);
    this.initPhoneNumber();
    this.initProcedure();
    this.initVoiceAndVoicemail();
    this.initHandoverConfig();
    this.initQuietHours();
    this.loadProcedures();
  }

  get isEditing() {
    return !!this.args.campaign;
  }

  get isDraft() {
    return !this.args.campaign || this.args.campaign.status === 'draft';
  }

  get panelTitle() {
    return this.isEditing
      ? this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.edit-title')
      : this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.new-title');
  }

  get phoneNumberOptions(): { value: string; text: string }[] {
    let phoneNumbers = this.args.phoneNumbers;
    if (!phoneNumbers) {
      return [];
    }

    return Object.entries(phoneNumbers)
      .filter(([, phoneNumber]) => phoneNumber.outboundEnabled)
      .map(([id, phoneNumber]) => {
        let number = phoneNumber.formattedPhoneNumber || phoneNumber.phoneNumber;
        return {
          value: id,
          text: phoneNumber.name ? `${phoneNumber.name} (${number})` : number,
        };
      });
  }

  get voiceOptions(): { value: string; text: string; description: string }[] {
    return getVoicesForLanguage('en-US').map((voice) => ({
      value: voice.value,
      text: voice.text,
      description: voice.description.map((key) => this.intl.t(key)).join(' '),
    }));
  }

  get timeOptions() {
    return TIME_OPTIONS;
  }

  get selectedVoiceDescription(): string {
    if (!this.selectedVoice) {
      return '';
    }
    let voice = FIN_VOICE_11LABS_VOICES.find((v) => v.value === this.selectedVoice);
    if (!voice) {
      return '';
    }
    return voice.description.map((key) => this.intl.t(key)).join(' ');
  }

  get assignableAdmins() {
    return this.appService.app.assignableAdmins;
  }

  get handoverOptions(): { icon: string; value: string; text: string }[] {
    return [
      {
        icon: 'phone-hold',
        value: 'handover-to-human',
        text: this.intl.t(
          'ai-agent.deploy.phone-overview.outbound-campaigns.handover-to-human-option',
        ),
      },
      {
        icon: 'phone-transfer',
        value: 'handover-to-forward-call',
        text: this.intl.t(
          'ai-agent.deploy.phone-overview.outbound-campaigns.handover-to-forward-call-option',
        ),
      },
    ];
  }

  get relevantHandoverConfig(): HandoverConfiguration | undefined {
    let config = this.clonedHandoverConfig[this.configBeingEdited];
    return config ?? this.clonedHandoverConfig['default'];
  }

  get handoverType(): string | undefined {
    return this.relevantHandoverConfig?.type;
  }

  get handoverAssigneeId(): string | undefined {
    let config = this.relevantHandoverConfig;
    if (config?.type === 'handover-to-human' || config?.type === 'handover-to-forward-call') {
      return config.config?.assigneeId;
    }
    return undefined;
  }

  get deflectionTimer(): number | undefined {
    let config = this.relevantHandoverConfig;
    if (config?.type === 'handover-to-human') {
      return (config.config?.deflectionTimer || 300) / 60;
    }
    return undefined;
  }

  get forwardToNumber(): string | undefined {
    let config = this.relevantHandoverConfig;
    if (config?.type === 'handover-to-forward-call') {
      return config.config?.forwardToNumber;
    }
    return undefined;
  }

  get closeConversationOnForward(): boolean {
    let config = this.relevantHandoverConfig;
    if (config?.type === 'handover-to-forward-call') {
      return config.config?.closeConversationOnForward ?? false;
    }
    return false;
  }

  get goodbyeMessage(): string | undefined {
    let config = this.relevantHandoverConfig;
    if (config?.type === 'handover-to-human') {
      return (
        config.config?.goodbyeMessage ??
        this.intl.t(
          'ai-agent.deploy.phone-overview.outbound-campaigns.goodbye-message-placeholder-human',
        )
      );
    }
    if (config?.type === 'handover-to-forward-call') {
      return (
        config.config?.goodbyeMessage ??
        this.intl.t(
          'ai-agent.deploy.phone-overview.outbound-campaigns.goodbye-message-placeholder-forward',
        )
      );
    }
    return undefined;
  }

  get hasVoicemailValidationError(): boolean {
    return this.voicemailEnabled && !this.voicemailMessage.trim();
  }

  get hasQuietHoursValidationError(): boolean {
    return this.quietHoursEnabled && this.quietHoursEnd <= this.quietHoursStart;
  }

  get handoverConfigurationPayload(): FinVoiceNumberConfigurationHandoverConfiguration {
    let config: FinVoiceNumberConfigurationHandoverConfiguration = {};

    if (this.clonedHandoverConfig.in) {
      config.in = this.clonedHandoverConfig.in;
    }

    if (this.clonedHandoverConfig.nin) {
      config.nin = this.clonedHandoverConfig.nin;
    }

    if (this.validSelectedProcedureId) {
      config.procedureId = this.validSelectedProcedureId;
    }

    config.autoCloseWhenNotEscalated = this.autoCloseWhenNotEscalated;

    if (this.defaultTeamId) {
      config.defaultTeamId = this.defaultTeamId;
    }

    return config;
  }

  get campaignPayload(): Record<string, unknown> {
    return {
      app_id: this.appService.app.id,
      name: this.campaignName.trim(),
      workspace_phone_number_id: this.validSelectedPhoneNumberId,
      procedure_id: this.validSelectedProcedureId,
      handover_configuration: this.handoverConfigurationPayload,
      voice: this.selectedVoice,
      voicemail_enabled: this.voicemailEnabled,
      voicemail_message: this.voicemailMessage.trim() || undefined,
      quiet_hours_enabled: this.quietHoursEnabled,
      quiet_hours_start: this.quietHoursStart,
      quiet_hours_end: this.quietHoursEnd,
    };
  }

  get canSaveDraft(): boolean {
    return !!this.campaignName.trim();
  }

  get canSave(): boolean {
    return (
      this.canSaveDraft && !!this.validSelectedPhoneNumberId && !!this.validSelectedProcedureId
    );
  }

  get canSend(): boolean {
    return (
      this.isDraft &&
      this.canSave &&
      this.isParsed &&
      this.parsedRows.length > 0 &&
      this.validationErrors.length === 0 &&
      !this.hasVoicemailValidationError &&
      !this.hasQuietHoursValidationError
    );
  }

  get rowCount() {
    return this.parsedRows.length;
  }

  get hasValidationErrors() {
    return this.validationErrors.length > 0;
  }

  get hasExistingCsvData(): boolean {
    return !this.isDraft && !!this.args.campaign && this.args.campaign.total_rows > 0;
  }

  get sendConfirmBody() {
    return this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.send-confirm', {
      count: this.parsedRows.length,
    });
  }

  get procedureOptions(): { value: string; text: string }[] {
    return this.procedures.map((proc) => ({
      value: proc.id,
      text: proc.name,
    }));
  }

  get validSelectedPhoneNumberId(): string | undefined {
    if (!this.selectedPhoneNumberId) {
      return undefined;
    }
    return this.phoneNumberOptions.some((o) => o.value === this.selectedPhoneNumberId)
      ? this.selectedPhoneNumberId
      : undefined;
  }

  get selectedPhoneNumberText(): string {
    if (!this.validSelectedPhoneNumberId) {
      return this.intl.t(
        'ai-agent.deploy.phone-overview.outbound-campaigns.assigned-to-placeholder',
      );
    }
    let option = this.phoneNumberOptions.find((o) => o.value === this.validSelectedPhoneNumberId);
    return option?.text || '';
  }

  get isPhoneNumberPlaceholder(): boolean {
    return !this.validSelectedPhoneNumberId;
  }

  get selectedProcedureText(): string {
    if (!this.validSelectedProcedureId) {
      return this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.procedure-placeholder');
    }
    let option = this.procedureOptions.find((o) => o.value === this.validSelectedProcedureId);
    return option?.text || '';
  }

  get isProcedurePlaceholder(): boolean {
    return !this.validSelectedProcedureId;
  }

  get validSelectedProcedureId(): string | undefined {
    if (!this.selectedProcedureId) {
      return undefined;
    }
    return this.procedures.some((p) => p.id === this.selectedProcedureId)
      ? this.selectedProcedureId
      : undefined;
  }

  private initPhoneNumber() {
    if (!this.args.campaign?.workspace_phone_number_id) {
      return;
    }
    this.selectedPhoneNumberId = String(this.args.campaign.workspace_phone_number_id);
  }

  private initProcedure() {
    if (this.args.campaign?.procedure_id) {
      this.selectedProcedureId = this.args.campaign.procedure_id;
    }
  }

  private initVoiceAndVoicemail() {
    if (this.args.campaign?.voice) {
      this.selectedVoice = this.args.campaign.voice;
    }
    if (this.args.campaign) {
      this.voicemailEnabled = this.args.campaign.voicemail_enabled || false;
      this.voicemailMessage = this.args.campaign.voicemail_message || '';
    }
  }

  private initHandoverConfig() {
    let handoverConfig = this.args.campaign?.handover_configuration;
    if (!handoverConfig) {
      return;
    }

    let cloned: FinVoiceNumberConfigurationHandoverConfiguration = {};

    if (handoverConfig.in) {
      let { type, config } = handoverConfig.in;
      cloned.in = { type, config: { ...(config ?? {}) } } as HandoverConfiguration;
    }

    if (handoverConfig.nin) {
      let { type, config } = handoverConfig.nin;
      cloned.nin = { type, config: { ...(config ?? {}) } } as HandoverConfiguration;
    }

    this.clonedHandoverConfig = cloned;
    this.autoCloseWhenNotEscalated = handoverConfig.autoCloseWhenNotEscalated ?? false;
    this.defaultTeamId = handoverConfig.defaultTeamId;
  }

  private initQuietHours() {
    if (this.args.campaign) {
      this.quietHoursEnabled = this.args.campaign.quiet_hours_enabled ?? true;
      this.quietHoursStart = this.args.campaign.quiet_hours_start ?? '09:00';
      this.quietHoursEnd = this.args.campaign.quiet_hours_end ?? '21:00';
    }
  }

  private async loadProcedures() {
    this.isLoadingProcedures = true;
    try {
      let response = await get('/ember/fin_voice/procedures', {
        app_id: this.appService.app.id,
        state: 'live',
      });
      this.procedures = response as FinVoiceProcedure[];
    } catch {
      this.procedures = [];
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.procedure-load-error'),
      );
    } finally {
      this.isLoadingProcedures = false;
    }
  }

  @action
  onNameChange(event: Event) {
    this.campaignName = (event.target as HTMLInputElement).value;
  }

  @action
  onPhoneNumberSelect(value: string) {
    this.selectedPhoneNumberId = value || undefined;
  }

  @action
  onProcedureSelect(value: string) {
    this.selectedProcedureId = value || undefined;
  }

  @action
  onVoiceSelect(value: string) {
    this.selectedVoice = value || undefined;
  }

  @action
  onVoicemailToggle() {
    this.voicemailEnabled = !this.voicemailEnabled;
  }

  @action
  onVoicemailMessageChange(event: Event) {
    this.voicemailMessage = (event.target as HTMLTextAreaElement).value;
  }

  @action
  changeConfigBeingEdited(config: ScheduledConfigurationKey) {
    this.configBeingEdited = config;
  }

  @action
  onQuietHoursToggle() {
    this.quietHoursEnabled = !this.quietHoursEnabled;
  }

  @action
  onQuietHoursStartChange(value: string) {
    this.quietHoursStart = value;
  }

  @action
  onQuietHoursEndChange(value: string) {
    this.quietHoursEnd = value;
  }

  @action
  setHandoverType(type: HandoverConfigurationTools) {
    if (this.relevantHandoverConfig) {
      this.relevantHandoverConfig.type = type;
      this.relevantHandoverConfig.config = {};
    } else {
      this.clonedHandoverConfig[this.configBeingEdited] = {
        type,
        config: {},
      } as HandoverConfiguration;
    }
    this.refreshHandoverConfig();
  }

  @action
  setHandoverAssigneeId(assigneeId: string) {
    let config = this.relevantHandoverConfig;
    if (!config) {
      return;
    }
    if (config.type !== 'handover-to-human' && config.type !== 'handover-to-forward-call') {
      return;
    }
    if (!config.config) {
      config.config = {};
    }
    config.config.assigneeId = assigneeId;
    this.refreshHandoverConfig();
  }

  @action
  setDeflectionTimer(event: Event) {
    let value = Number((event.target as HTMLInputElement).value);
    let config = this.relevantHandoverConfig;
    if (config?.type === 'handover-to-human' && config.config) {
      config.config.deflectionTimer = value * 60;
      this.refreshHandoverConfig();
    }
  }

  @action
  setForwardToNumber(event: Event) {
    let value = (event.target as HTMLInputElement).value;
    let config = this.relevantHandoverConfig;
    if (config?.type === 'handover-to-forward-call' && config.config) {
      config.config.forwardToNumber = value;
      this.refreshHandoverConfig();
    }
  }

  @action
  toggleCloseConversationOnForward() {
    let config = this.relevantHandoverConfig;
    if (config?.type === 'handover-to-forward-call' && config.config) {
      config.config.closeConversationOnForward = !config.config.closeConversationOnForward;
      this.refreshHandoverConfig();
    }
  }

  @action
  setGoodbyeMessage(event: Event) {
    let value = (event.target as HTMLTextAreaElement).value;
    let config = this.relevantHandoverConfig;
    if (!config?.config) {
      return;
    }
    if (config.type === 'handover-to-human' || config.type === 'handover-to-forward-call') {
      config.config.goodbyeMessage = value;
      this.refreshHandoverConfig();
    }
  }

  @action
  onAutoCloseToggle() {
    this.autoCloseWhenNotEscalated = !this.autoCloseWhenNotEscalated;
  }

  @action
  onDefaultTeamSelect(value: string) {
    this.defaultTeamId = value || undefined;
  }

  @action
  onBlanketClick(event: MouseEvent) {
    if ((event.target as HTMLElement).hasAttribute('data-side-drawer-blanket')) {
      this.args.onClose();
    }
  }

  @action
  onCsvFileSet(file: File | null) {
    this.file = file;
    this.fileIsValid = file !== null;
    this.isParsed = false;
    this.parsedRows = [];
    this.validationErrors = [];
    this.parsingErrorMessage = '';
    this.fileFailedToUpload = false;

    if (file) {
      this.parseCsvFile();
    }
  }

  @action
  async parseCsvFile() {
    if (!this.file) {
      return;
    }

    try {
      this.parsingErrorMessage = '';
      this.validationErrors = [];
      let results = await this.parseRawCsvFile(this.file);

      if (results.data.length === 0) {
        this.parsingErrorMessage = this.intl.t(
          'ai-agent.deploy.phone-overview.outbound-csv-upload.validation-error-missing-phone',
        );
        this.fileFailedToUpload = true;
        return;
      }

      let headers = results.meta.fields || [];

      if (!headers.includes('phone_number')) {
        this.parsingErrorMessage = this.intl.t(
          'ai-agent.deploy.phone-overview.outbound-csv-upload.validation-error-missing-phone',
        );
        this.fileFailedToUpload = true;
        return;
      }

      if (results.data.length > MAX_CSV_ROWS) {
        this.parsingErrorMessage = this.intl.t(
          'ai-agent.deploy.phone-overview.outbound-csv-upload.validation-error-row-limit',
          { limit: MAX_CSV_ROWS },
        );
        this.fileFailedToUpload = true;
        return;
      }

      let rows: { phone_number: string }[] = [];
      let seenPhoneNumbers = new Set<string>();
      let errors: string[] = [];

      for (let i = 0; i < results.data.length; i++) {
        let row = results.data[i];
        let phoneNumber = row['phone_number']?.trim() ?? '';

        if (!this.E164_REGEX.test(phoneNumber)) {
          errors.push(
            this.intl.t(
              'ai-agent.deploy.phone-overview.outbound-csv-upload.validation-error-invalid-phone',
              { row: i + 1 },
            ),
          );
        } else if (seenPhoneNumbers.has(phoneNumber)) {
          errors.push(
            this.intl.t(
              'ai-agent.deploy.phone-overview.outbound-csv-upload.validation-error-duplicate-phone',
              { row: i + 1, phone: phoneNumber },
            ),
          );
        } else {
          seenPhoneNumbers.add(phoneNumber);
          rows.push({ phone_number: phoneNumber });
        }
      }

      this.validationErrors = errors;
      this.parsedRows = rows;
      this.isParsed = true;
    } catch (e: unknown) {
      this.fileFailedToUpload = true;
      this.parsingErrorMessage = this.intl.t(
        'ai-agent.deploy.phone-overview.outbound-csv-upload.validation-error-missing-phone',
      );
    }
  }

  @action
  async saveDraft() {
    if (!this.canSaveDraft) {
      return;
    }

    this.isSaving = true;
    try {
      await this.saveOrCreateCampaign();

      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.save-success'),
      );
      this.args.onSave();
    } catch (e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.save-error'),
      );
    } finally {
      this.isSaving = false;
    }
  }

  @action
  openSendConfirmModal() {
    if (!this.canSend) {
      return;
    }
    this.showSendConfirmModal = true;
  }

  @action
  closeSendConfirmModal() {
    this.showSendConfirmModal = false;
  }

  @action
  async confirmSendCampaign() {
    this.showSendConfirmModal = false;

    if (!this.canSend || !this.file) {
      return;
    }

    this.isSending = true;
    try {
      let campaignId = await this.saveOrCreateCampaign();

      // Send the campaign with the CSV
      let formData = new FormData();
      formData.append('app_id', this.appService.app.id);
      formData.append('csv_file', this.file);

      await ajax({
        type: 'POST',
        url: `/ember/fin_voice/outbound_campaigns/${campaignId}/send_campaign`,
        data: formData,
        processData: false,
        contentType: false,
      });

      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.send-success'),
      );
      this.args.onSave();
    } catch (e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.deploy.phone-overview.outbound-campaigns.send-error'),
      );
    } finally {
      this.isSending = false;
    }
  }

  private async saveOrCreateCampaign(): Promise<number> {
    let campaignId: number;

    if (this.isEditing) {
      campaignId = this.args.campaign!.id;
      await ajax({
        type: 'PUT',
        url: `/ember/fin_voice/outbound_campaigns/${campaignId}`,
        data: JSON.stringify(this.campaignPayload),
      });
    } else {
      let response: { campaign: { id: number } } = await ajax({
        type: 'POST',
        url: '/ember/fin_voice/outbound_campaigns',
        data: JSON.stringify(this.campaignPayload),
      });
      campaignId = response.campaign.id;
    }

    return campaignId;
  }

  private refreshHandoverConfig() {
    this.clonedHandoverConfig = { ...this.clonedHandoverConfig };
  }

  private async parseRawCsvFile(file: File): Promise<ParseResult<Record<string, string>>> {
    return new EmberPromise((resolve, reject) => {
      let complete = (results: ParseResult<Record<string, string>>) => {
        if (results.errors.length) {
          if (
            results.errors.some((error: { code: string }) => error.code !== 'UndetectableDelimiter')
          ) {
            reject(results);
            return;
          }
        }
        resolve(results);
      };
      csvParser.parse(file, { header: true, skipEmptyLines: true, complete, error: reject });
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Deploy::Voice::OutboundCampaignEditPanel': typeof OutboundCampaignEditPanel;
    'ai-agent/deploy/voice/outbound-campaign-edit-panel': typeof OutboundCampaignEditPanel;
  }
}
